import { LEAD_DROP_POPUP_5002_CONSTANTS } from "../leadDropPopup/leadDropPopup_5002";

/**
 * Lead Drop Form 5002 — embeddable (inline) twin of Lead Drop Popup 5002.
 *
 * Same BDU single-step flow (all five fields on one screen, OTP revealed inline,
 * then the last card) as popup 5002, configured with its OWN row so the inline form
 * and the modal popup hold independent settings.
 *
 * Fields that only make sense for a modal are stripped: the display intervals and
 * delay/exit toggles (an inline form is always rendered), the close icon, the
 * success button link (the inline last card has nothing to navigate back from),
 * and the side image (the embedded card is a single narrow column, matching BDU).
 */
const POPUP_INTERNAL_FIELDS = new Set([
  "popup_display_interval_1",
  "popup_display_interval_2",
  "popup_display_interval_3",
  "enable_delay_form",
  "enable_exit_form",
  "close_icon",
  "close_icon_alt",
  "success_button_link",
  "side_image",
  "side_image_alt",
  "side_image_overlay_color",
]);

/**
 * The fields config is inherited from popup 5002, whose field names map to
 * `lead_drop_popup` columns. `lead_drop_form` has a DIFFERENT column set, so any
 * inherited popup-column field saved top-level would be silently dropped by
 * Sequelize. Only these names are real columns on BOTH tables; every other
 * inherited field is flagged `isJsonField` so Form_4 nests it under
 * `additional_fields`, which the server persists and flattens back for the widget.
 *
 * Note `form_title` / `form_sub_text` are NOT here — the form table calls those
 * `title` / `sub_text`, so they ride in `additional_fields` like everything else.
 */
const LEAD_DROP_FORM_SHARED_COLUMNS = new Set([
  "checkbox_description",
  "checkbox_color",
  "terms_conditions_link",
  "privacy_policy_link",
  "button_text",
  "button_text_color",
  "button_background_color",
]);

const intoAdditionalFieldsUnlessFormColumn = <
  T extends { fieldName?: unknown; isJsonField?: boolean },
>(
  field: T,
): T =>
  typeof field.fieldName === "string" &&
  !LEAD_DROP_FORM_SHARED_COLUMNS.has(field.fieldName) &&
  !field.isJsonField
    ? { ...field, isJsonField: true }
    : field;

const stripPopupInternalKeys = (row: Record<string, unknown>) => {
  const next: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(row)) {
    if (!POPUP_INTERNAL_FIELDS.has(key)) next[key] = value;
  }
  return next;
};

export const LEAD_DROP_FORM_5002_CONSTANTS = {
  DEFAULT_ROW: stripPopupInternalKeys(
    LEAD_DROP_POPUP_5002_CONSTANTS.DEFAULT_ROW as Record<string, unknown>,
  ),

  LEAD_DROP_FORM_FIELDS_CONFIG:
    LEAD_DROP_POPUP_5002_CONSTANTS.LEAD_DROP_POPUP_FIELDS_CONFIG.filter(
      (field) =>
        typeof field.fieldName !== "string" ||
        !POPUP_INTERNAL_FIELDS.has(field.fieldName),
    ).map(intoAdditionalFieldsUnlessFormColumn),

  METADATA_FIELDS: LEAD_DROP_POPUP_5002_CONSTANTS.METADATA_FIELDS,
};
